import {useQuery, useQueryClient, useMutation} from "react-query";
import {toast} from 'react-toastify';
import API from './utils/api';
import {
    SalaryAdjustmentDto,
    SalaryAdjustmentPaginator,
    SalaryAdjustmentStatus,
    CreateSalaryAdjustmentDto,
    QuerySalaryAdjustmentDto,
    UpdateSalaryAdjustmentDto
} from "../models/salary-adjustment.dto";
import ENDPOINTS from "./utils/endpoints";

/** Poll interval used while an adjustment is being (or about to be) applied */
const IN_PROGRESS_REFETCH_INTERVAL = 5000;

/**
 * True when a row is expected to change status shortly: it is being processed,
 * or it was created without a pay period (applied immediately in the background).
 */
export const isAdjustmentInProgress = (adjustment: SalaryAdjustmentDto) =>
    adjustment.status === SalaryAdjustmentStatus.PROCESSING ||
    (adjustment.status === SalaryAdjustmentStatus.PENDING && !adjustment.payPeriodId);

function useInvalidateSalaryAdjustments() {
    const queryClient = useQueryClient();

    return () => {
        queryClient.invalidateQueries(ENDPOINTS.PROCESSING_COMPANY.SALARY_ADJUSTMENT.GET);
        queryClient.invalidateQueries(ENDPOINTS.PROCESSING_COMPANY.SALARY_ADJUSTMENT.DETAIL);
    };
}

export function useGetSalaryAdjustment(companyId: number, options?: QuerySalaryAdjustmentDto) {
    const url = ENDPOINTS.PROCESSING_COMPANY.SALARY_ADJUSTMENT.GET;

    const { data, isLoading, isFetching, error, refetch } = useQuery<SalaryAdjustmentPaginator, Error>(
        [url, companyId, options],
        () => API.ProcessingCompanyAPI.getSalaryAdjustments(companyId, options),
        {
            enabled: !!companyId,
            keepPreviousData: true,
            refetchInterval: (response) =>
                response?.data?.some(isAdjustmentInProgress)
                    ? IN_PROGRESS_REFETCH_INTERVAL
                    : false,
        }
    );

    return { data, isLoading, isFetching, error, refetch }
}

export function useGetSalaryAdjustmentById(companyId: number, id: number) {
    const url = ENDPOINTS.PROCESSING_COMPANY.SALARY_ADJUSTMENT.DETAIL;

    const {data, isLoading, error} = useQuery<{ data: SalaryAdjustmentDto }, Error>(
        [url, companyId, id],
        () => API.ProcessingCompanyAPI.getSalaryAdjustmentById(companyId, id),
        {
            enabled: !!(companyId && id),
            refetchInterval: (response) =>
                response?.data && isAdjustmentInProgress(response.data)
                    ? IN_PROGRESS_REFETCH_INTERVAL
                    : false,
        }
    );

    return {
        data: data,
        isLoading,
        error,
    };
}

export function useCreateSalaryAdjustment() {
    const invalidate = useInvalidateSalaryAdjustments();

    return useMutation(
        ({companyId, payload}: {companyId: number, payload: CreateSalaryAdjustmentDto}) => API.ProcessingCompanyAPI.createSalaryAdjustment(companyId, payload),
        {
            onSuccess: (_data, {payload}) => {
                toast.success(payload.payPeriodId
                    ? 'Salary adjustment saved. It will be applied automatically when the selected pay period starts.'
                    : 'Salary adjustment saved and is being applied. Its status will update when it is done.');
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        }
    )
}

export function useUpdateSalaryAdjustment() {
    const invalidate = useInvalidateSalaryAdjustments();

    return useMutation(
        ({payload, companyId, id}: {payload: UpdateSalaryAdjustmentDto, companyId: number, id: number}) => API.ProcessingCompanyAPI.updateSalaryAdjustment(companyId, id, payload),
        {
            onSuccess: (_data, {payload}) => {
                toast.success(payload.payPeriodId
                    ? 'Salary adjustment updated. It will be applied automatically when the selected pay period starts.'
                    : 'Salary adjustment updated successfully');
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        }
    )
}

export function useDeleteSalaryAdjustment() {
    const invalidate = useInvalidateSalaryAdjustments();

    return useMutation(
        ({companyId, id}: {companyId: number, id: number}) => API.ProcessingCompanyAPI.deleteSalaryAdjustmentById(companyId, id),
        {
            onSuccess: () => {
                toast.success('Record deleted successfully');
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        }
    )
}

export function useReverseSalaryAdjustment() {
    const invalidate = useInvalidateSalaryAdjustments();

    return useMutation(
        ({companyId, id}: {companyId: number, id: number}) => API.ProcessingCompanyAPI.reverseSalaryAdjustment(companyId, id),
        {
            onSuccess: (response: { data: SalaryAdjustmentDto }) => {
                const skipped = response?.data?.reversalReport
                    ?.split('\n').filter(Boolean).length;
                if (skipped) {
                    toast.warning(
                        `Salary adjustment undone, but ${skipped} item(s) were skipped because they changed since it was applied. See its details for the list.`
                    );
                } else {
                    toast.success('Salary adjustment undone successfully');
                }
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        }
    )
}
